import { RxLinkBreak2 } from '@react-icons/all-files/rx/RxLinkBreak2'
import cuid from 'cuid'
import isEqual from 'lodash/isEqual'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { AIMS, UNIFIED_SEARCH_ENABLE } from '@disco/constants/FeatureTypes'
import {
  SEARCH_APPEARS_ON_COLUMN_FIELD,
  SEARCH_ARTIST_COLUMN_FIELD,
  SEARCH_INDICATORS_COLUMN_FIELD,
  SEARCH_SOURCE_COLUMN_FIELD,
  SEARCH_TITLE_COLUMN_FIELD,
} from '@disco/constants/search'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  fetchNextPage,
  fetchResults,
  initializeDefaultColumnVisibility,
  selectSearchPage,
  selectSearchPageResults,
  selectSearchPageSearchId,
  setColumnOrder,
  setColumnSizing,
  setColumnVisibility,
  setRowSelection,
  setSorting,
} from '@disco/state-management/reducers/search-page'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { selectSearchPagePlayableTrackIds } from '@disco/state-management/selectors/media'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { getMusicProviderFromUrl } from '@disco/utils/links'
import {
  sendMixpanelSearchEvent,
  sendSearchSortingEvent,
} from '@disco/utils/mixpanel'
import { getApiSearchType } from '@disco/utils/search-page'
import { isPlayableTrack } from '@disco/utils/tracks'

import type {
  ColumnOrderState,
  ColumnSizingState,
  SortingState,
  VisibilityState,
} from 'types/data-table'
import type { FilterState } from 'types/filter-page'
import type { SearchType, SourceScope } from 'types/search-page'

import { SelectionProvider } from 'contexts/selection'

import DocumentHeadTags from 'components/document-head-tags'
import { Button, DataTable, DiscoCallout, Flex } from 'components/ds'
import { insertColumnChain } from 'components/ds/DataTable/table-utils'
import type { SearchTypeOption } from 'components/ds/SearchTypeSelector'
import ExplorerActionMenu from 'components/explorer/action-menu'
import SimilarityPlayer from 'components/search/similarity-player'
import SimilarityPlayerSkeleton from 'components/search/similarity-player/skeleton'

import { useDispatch, useSelector } from 'hooks/use-redux'

import SearchBar from './search-bar'
import SearchFilters from './search-filters'
import SearchHelpModal from './search-help-modal'
import { doesContextMatchQuery } from './utils/context-match'
import {
  getSearchResultsSource,
  getTrackActions,
  useMdsRowSelectionCondition,
  useSearchTableColumns,
} from './utils/index'
import { useIndicatorsEnabled } from './utils/use-indicators'
import { useNestedTrackRows } from './utils/use-nested-track-rows'
import { useNestedTracksEnabled } from './utils/use-nested-tracks'
import { usePromptSearchEnabled } from './utils/use-prompt-search-enabled'
import { useSegmentSelectionEnabled } from './utils/use-segment-selection'
import { useSimSearchEnabled } from './utils/use-sim-search-enabled'

export const ENABLE_NEW_SEARCH_PAGE = 'ENABLE_NEW_SEARCH_PAGE'

const SearchPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchPageState = useSelector(selectSearchPage)
  const simSearchEnabled = useSimSearchEnabled()
  const {
    query,
    searchType,
    isLoading,
    isLoadingNextPage,
    hasMore,
    rowSelection,
    columnVisibility,
    columnOrder,
    columnSizing,
    columnPinning,
    sorting: keywordSorting,
    aiSorting,
    filters,
    sourceScope,
    context,
    initialFetchLoaded,
    error: apiError,
    linkInfo,
    cursor,
    nextPageErrorCursor,
    submittedSearch,
  } = searchPageState
  // A type switch doesn't refetch, so show the sort of the results on screen.
  const resultsType = submittedSearch?.type ?? getApiSearchType(searchType)
  const sorting = resultsType === 'regular' ? keywordSorting : aiSorting

  const results = useSelector(selectSearchPageResults)
  const searchId = useSelector(selectSearchPageSearchId)
  const columns = useSearchTableColumns()
  const rowSelectionCondition = useMdsRowSelectionCondition()
  const indicatorsEnabled = useIndicatorsEnabled()
  const nestedTracksEnabled = useNestedTracksEnabled()

  const {
    rows: resultRows,
    visibleRows,
    expanded,
    onExpandedChange,
    getSubRows,
  } = useNestedTrackRows({
    results,
    searchId,
    enabled: nestedTracksEnabled,
  })

  const effectiveColumnOrder = useMemo(
    () =>
      insertColumnChain(
        columnOrder,
        [
          SEARCH_SOURCE_COLUMN_FIELD,
          SEARCH_ARTIST_COLUMN_FIELD,
          SEARCH_TITLE_COLUMN_FIELD,
        ],
        [
          { id: SEARCH_INDICATORS_COLUMN_FIELD, enabled: indicatorsEnabled },
          { id: SEARCH_APPEARS_ON_COLUMN_FIELD, enabled: true },
        ],
      ),
    [columnOrder, indicatorsEnabled],
  )

  // Get playable track IDs once at the page level to pass to column components
  const resultsTracklist = useSelector(selectSearchPagePlayableTrackIds)

  const tracklist = useMemo(
    () =>
      nestedTracksEnabled
        ? visibleRows.filter(isPlayableTrack).map(track => track.id)
        : resultsTracklist,
    [nestedTracksEnabled, visibleRows, resultsTracklist],
  )

  const aimsEnabled = useSelector(state =>
    isFeatureEnabled(state, { featureName: AIMS }),
  )

  const unifiedSearchEnabled = Boolean(
    useSelector(state => getConstanceSetting(state, UNIFIED_SEARCH_ENABLE)),
  )

  const promptSearchEnabled = usePromptSearchEnabled()

  // Segment feature flag
  const segmentSelectionEnabled = useSegmentSelectionEnabled()

  // Track the type of search error to show appropriate message
  const [searchErrorType, setSearchErrorType] = useState<
    'unsupported' | 'invalid' | null
  >(null)
  const prevIsLoadingRef = useRef(false)
  const [searchContext, setSearchContext] = useState<typeof context>(context)

  // Detect search errors after search completes (isLoading goes from true to false)
  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current
    prevIsLoadingRef.current = isLoading

    // Only check when search just completed (loading finished)
    if (wasLoading && !isLoading && searchType === 'similarity' && query) {
      if (apiError) {
        // If error says "unsupported" but the URL is from a supported platform,
        // it's actually an invalid/malformed URL, not an unsupported platform
        const isUnsupported =
          String(apiError).toLowerCase().includes('unsupported') &&
          getMusicProviderFromUrl(query) === null
        setSearchErrorType(isUnsupported ? 'unsupported' : 'invalid')
      } else {
        setSearchErrorType(null)
      }
    }
  }, [isLoading, searchType, query, apiError])

  // Reset error when query is cleared or search type changes
  useEffect(() => {
    if (!query || searchType !== 'similarity') {
      setSearchErrorType(null)
    }
  }, [query, searchType])

  // Create search type options based on feature flags
  const searchTypeOptions = useMemo(() => {
    const options: SearchTypeOption<SearchType>[] = [
      { value: 'keyword', label: 'Keyword' },
    ]

    if (simSearchEnabled) {
      options.push({ value: 'similarity', label: 'Similarity' })
    }

    if (promptSearchEnabled) {
      options.push({ value: 'prompt', label: 'Prompt' })
    }

    return options
  }, [promptSearchEnabled, simSearchEnabled])

  const handleRowSelectionChange = useCallback(
    (value: Record<string, boolean>) => {
      dispatch(setRowSelection(value))
    },
    [dispatch],
  )

  // Selection is positional, so expanding invalidates it. Keyed off the
  // rendered rows, not the click: children can land after the toggle.
  const prevVisibleRowIdsRef = useRef('')
  useEffect(() => {
    // Off means nothing reorders, and the page's existing resets still apply.
    if (!nestedTracksEnabled) return

    const ids = visibleRows.map(row => row.id).join(',')
    const previous = prevVisibleRowIdsRef.current
    prevVisibleRowIdsRef.current = ids

    if (!previous || ids === previous) return
    // An appended page leaves every existing position valid.
    if (ids.startsWith(`${previous},`)) return

    dispatch(resetSelection())
    dispatch(setRowSelection({}))
  }, [nestedTracksEnabled, visibleRows, dispatch])

  const startSearchTime = useMemo(() => new Date(), [])

  // Memoize handlers to prevent unnecessary re-renders
  const handlers = useMemo(
    () => ({
      handleColumnVisibilityChange: (value: VisibilityState) => {
        dispatch(setColumnVisibility(value))
      },
      // Fetches here rather than in the change effect, so a sim/prompt search
      // resetting its sort or a search type switch doesn't count as a re-sort.
      handleSortingChange: (value: SortingState) => {
        dispatch(setSorting(value))
        dispatch(resetSelection())
        dispatch(fetchResults({ resort: true }))
        sendSearchSortingEvent(value, startSearchTime, 'Search Page')
      },
      handleColumnOrderChange: (value: ColumnOrderState) => {
        dispatch(setColumnOrder(value))
      },
      handleColumnSizingChange: (value: ColumnSizingState) => {
        dispatch(setColumnSizing(value))
      },
    }),
    [dispatch, startSearchTime],
  )

  const getTrackActionsCallback = useCallback(
    (track: Parameters<typeof getTrackActions>[0]) =>
      getTrackActions(track, navigate, dispatch, simSearchEnabled),
    [navigate, dispatch, simSearchEnabled],
  )

  // A failed page leaves the scroll sentinel visible, so an unguarded observer
  // refires the same failing request indefinitely. Blocking on the failed cursor
  // rather than on any error keeps the rest of pagination live and lets retry
  // through, since its pending clears the cursor.
  const nextPageFailed =
    nextPageErrorCursor !== null && nextPageErrorCursor === cursor

  const handleFetchNextPage = useCallback(() => {
    if (hasMore && !isLoadingNextPage && !nextPageFailed) {
      dispatch(fetchNextPage())
    }
  }, [dispatch, hasMore, isLoadingNextPage, nextPageFailed])

  const handleRetryNextPage = useCallback(() => {
    if (!isLoadingNextPage) {
      dispatch(fetchNextPage())
    }
  }, [dispatch, isLoadingNextPage])

  const lastFilters = useRef<FilterState[]>([])
  const lastSearchType = useRef<string>('keyword')
  const lastSourceScope = useRef<SourceScope[] | null>(null)
  const hasInitialized = useRef(false)

  // Temporary check to hide AIMS "Learn more" button
  const showAimsLearnMoreButton = false

  // Initialize default column visibility when columns are available
  useEffect(() => {
    dispatch(initializeDefaultColumnVisibility({ columns }))
  }, [columns, dispatch])

  useEffect(() => {
    // Initialize refs and optionally fetch on first load
    if (!hasInitialized.current) {
      hasInitialized.current = true
      // Initialize refs with current values
      lastFilters.current = filters
      lastSearchType.current = searchType
      lastSourceScope.current = sourceScope

      dispatch(setRowSelection({}))

      // Only fetch if we haven't loaded initial data yet
      if (!initialFetchLoaded) {
        dispatch(fetchResults())
      }
      return
    }

    // Only consider filters with actual values for change detection
    const currentFiltersWithValues = filters.filter(
      f => f.value !== undefined && f.value !== null,
    )
    const previousFiltersWithValues = lastFilters.current.filter(
      f => f.value !== undefined && f.value !== null,
    )
    const filtersChanged = !isEqual(
      currentFiltersWithValues,
      previousFiltersWithValues,
    )

    const searchTypeChanged = !isEqual(lastSearchType.current, searchType)
    const sourceScopeChanged = !isEqual(sourceScope, lastSourceScope.current)

    if (searchTypeChanged || filtersChanged || sourceScopeChanged) {
      dispatch(resetSelection())
    }
    // Only fetch if filters or source scope changed
    if (filtersChanged || sourceScopeChanged) {
      dispatch(fetchResults())

      if (filtersChanged) {
        sendMixpanelSearchEvent(
          { query, filters: searchPageState.filters },
          startSearchTime,
          'Search Page',
        )
      }

      // Update refs after fetch
      lastFilters.current = filters
      lastSourceScope.current = sourceScope
    }
    if (searchType === 'similarity') {
      setSearchContext({
        ...context,
        ...(linkInfo ? { disco_link_info: linkInfo } : {}),
      })
    } else {
      setSearchContext(context)
    }

    // Update search type ref without triggering search
    lastSearchType.current = searchType
  }, [
    filters,
    sourceScope,
    searchType,
    dispatch,
    initialFetchLoaded,
    query,
    startSearchTime,
    linkInfo,
    searchPageState.filters,
    context,
  ])

  const prevRowSelectionConditionRef = useRef(rowSelectionCondition)
  useEffect(() => {
    const prev = prevRowSelectionConditionRef.current
    prevRowSelectionConditionRef.current = rowSelectionCondition
    if (!prev && rowSelectionCondition) {
      dispatch(setRowSelection({}))
    }
  }, [rowSelectionCondition, dispatch])

  // Simple block for now if someone stumbles upon this page
  const enabled = useSelector(state =>
    getConstanceSetting(state, ENABLE_NEW_SEARCH_PAGE),
  )
  const [selectionId] = useState(cuid())

  // Determine if SimilarityPlayer should be shown and where
  const canShowSimilarityPlayer =
    context &&
    initialFetchLoaded &&
    query &&
    !searchErrorType &&
    searchType === 'similarity' &&
    getMusicProviderFromUrl(query)

  const showSimilarityPlayerInTopRow =
    segmentSelectionEnabled &&
    canShowSimilarityPlayer &&
    doesContextMatchQuery(searchContext, query)

  const showSimilarityPlayerAboveTable =
    !segmentSelectionEnabled && canShowSimilarityPlayer

  // Waveform skeleton shown while a similarity search loads, before the player
  // mounts; isLoading/context-match keeps it hidden while only editing the query.
  const similaritySearchActive =
    !searchErrorType &&
    !!query &&
    searchType === 'similarity' &&
    !!getMusicProviderFromUrl(query)

  const similarityWaveformLoading =
    similaritySearchActive &&
    (isLoading || doesContextMatchQuery(context, query))

  const showSimilarityWaveformSkeletonAboveTable =
    !segmentSelectionEnabled &&
    similarityWaveformLoading &&
    !showSimilarityPlayerAboveTable

  const showSimilarityWaveformSkeletonInTopRow =
    segmentSelectionEnabled &&
    similarityWaveformLoading &&
    !showSimilarityPlayerInTopRow

  if (!enabled) return null

  return (
    <div className="Page">
      <DocumentHeadTags title="Search results | DISCO" />
      <div className="Page_Content">
        <Flex
          direction="column"
          gap="3"
          height="100%"
          minHeight="0"
          flexBasis="1"
          overflow="auto"
        >
          <Flex
            justify="between"
            direction="row"
            align="start"
            gap="2"
            width="100%"
          >
            <Flex
              wrap="wrap"
              gap="2"
              width="100%"
              align="start"
              flexShrink="1 0 auto"
              minWidth="0"
            >
              <SearchBar
                searchTypeOptions={searchTypeOptions}
                hasError={!!searchErrorType}
              />
              <SearchFilters />
            </Flex>
            <Flex width="max-content" height="max-content" align="center">
              <SearchHelpModal />
            </Flex>
          </Flex>

          {searchErrorType && (
            <Flex width="100%" justify="center">
              <DiscoCallout
                type="error"
                variant="soft"
                size="1"
                icon={<RxLinkBreak2 />}
              >
                {searchErrorType === 'unsupported'
                  ? 'This link is currently not supported. Try tracks from DISCO, Spotify, YouTube and Apple Music.'
                  : "This link doesn't seem to be correct. Please check the URL and try again."}
              </DiscoCallout>
            </Flex>
          )}
          {aimsEnabled && (
            <DiscoCallout
              type="feature"
              dismissible
              dismissKey="aims-search-banner"
              variant="soft"
              size="1"
              highContrast={false}
            >
              <Flex align={'center'} gap="2">
                <strong>AIMS search is enabled.</strong>{' '}
                <span>
                  {unifiedSearchEnabled
                    ? 'Paste a link for Similarity Search or toggle on AI search to use a prompt.'
                    : 'To use it, select Similarity or Prompt in the search bar above.'}
                </span>{' '}
                {showAimsLearnMoreButton && (
                  <Button variant="solid" size="1" asChild>
                    <a
                      href="https://support.disco.ac/home/aims-search"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </Button>
                )}
              </Flex>
            </DiscoCallout>
          )}
          {showSimilarityWaveformSkeletonAboveTable && (
            <SimilarityPlayerSkeleton />
          )}
          {showSimilarityPlayerAboveTable && (
            <SimilarityPlayer
              key={searchContext?.waveform_url}
              context={searchContext}
              searchQuery={query}
            />
          )}
          <SelectionProvider
            value={{
              id: selectionId,
              data: searchErrorType ? [] : visibleRows.map(r => r),
              paneIndex: null,
            }}
          >
            <DataTable
              data={searchErrorType ? [] : resultRows}
              columns={columns}
              getSubRows={getSubRows}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              loading={isLoading}
              sorting={sorting}
              onSortingChange={handlers.handleSortingChange}
              columnVisibility={columnVisibility}
              columnOrder={effectiveColumnOrder}
              onColumnOrderChange={handlers.handleColumnOrderChange}
              enableColumnDragging
              columnSizing={columnSizing}
              onColumnSizingChange={handlers.handleColumnSizingChange}
              columnPinning={columnPinning}
              onColumnVisibilityChange={handlers.handleColumnVisibilityChange}
              rowSelection={rowSelection}
              rowSelectionCondition={rowSelectionCondition}
              onRowSelectionChange={handleRowSelectionChange}
              getRowActions={getTrackActionsCallback}
              hasNextPage={hasMore}
              isFetchingNextPage={isLoadingNextPage}
              onFetchNextPage={handleFetchNextPage}
              hasNextPageError={nextPageFailed}
              onRetryNextPage={handleRetryNextPage}
              location="search"
              isMultiSelect
              searchType={searchType}
              searchResultsSource={
                !aimsEnabled || searchType === 'keyword' ? 'DISCO' : 'AIMS'
              }
              searchId={searchId}
              meta={{ tracklist }}
              topRowContent={
                showSimilarityPlayerInTopRow ? (
                  <SimilarityPlayer
                    context={searchContext}
                    searchQuery={query}
                  />
                ) : showSimilarityWaveformSkeletonInTopRow ? (
                  <SimilarityPlayerSkeleton />
                ) : undefined
              }
            />
            <ExplorerActionMenu basePath="/search" />
          </SelectionProvider>
          {getSearchResultsSource(searchType, aimsEnabled)}
        </Flex>

        <Outlet />
      </div>
    </div>
  )
}

export default SearchPage
